import { useTranslation } from "next-i18next";

import { CollapsibleIntro } from "@/components/search/plp-seo/CollapsibleIntro";
import { FaqAccordion } from "@/components/search/plp-seo/FaqAccordion";
import { GuideCards } from "@/components/search/plp-seo/GuideCards";
import { LinkChips } from "@/components/search/plp-seo/LinkChips";
import { filterLinksAgainstDescription } from "@/components/search/plp-seo/editorial-links.utils";
import { getIconGuideLinks } from "@/components/search/plp-seo/icon-guides.utils";
import { splitListingDescription } from "@/components/search/plp-seo/listing-description.utils";
import {
  PLP_SEO_KEYS,
  plpSeoFallback,
} from "@/components/search/plp-seo/plp-seo.i18n";
import {
  PLP_SEO_SECTION_CLASS,
  PLP_SEO_SECTION_HEADING_SIZE,
  PLP_SEO_TEXT_WIDTH_CLASS,
} from "@/components/search/plp-seo/plp-seo.tokens";
import {
  type PlpEditorialLink,
  getPlpEditorialLinks,
} from "@/data/plp-editorial-links";
import { toContentLocale } from "@/i18n/config";
import { useContentLocale } from "@/i18n/hooks";
import { Heading } from "@/ui/cva/Heading";

export const PLP_SEO_HEADING_ID = "plp-seo-heading";
const GUIDES_HEADING_ID = "plp-editorial-links-heading";

export type PlpSeoBlockProps = {
  /** Strapi listing-page copy; all three may be missing. */
  heading?: string | null;
  subheading?: string | null;
  description?: string | null;
  /** Landing-page path without locale prefix, the editorial dataset key. */
  pagePath?: string | null;
  /**
   * WHO-5760: the one-line intro built from live data (`buildListingIntro`), shown only when
   * Strapi has no intro of its own, which is about 97% of listing pages.
   */
  generatedIntro?: string | null;
};

const isGuide = (link: PlpEditorialLink) => /\/blog\//.test(link.href);

/** Locales whose icon and glossary pages exist in the page language; see the block comment. */
const CHIP_LOCALES = new Set(["en"]);

export const showsEditorialChips = (locale: string | undefined) =>
  CHIP_LOCALES.has(toContentLocale(locale));

/**
 * The SEO block at the foot of a listing page, restructured per Evelien's design review
 * (WHO-5534): Strapi heading and subheading, the intro behind "Read more", the buyer questions
 * as an accordion, the editorial guides as cards, and the icon and glossary links as chips.
 *
 * Mounted once, in `features/search/components/SearchLayout.tsx`, since the legacy PLP went
 * (WHO-5636). It renders nothing when there is neither copy nor a dataset entry for the page.
 *
 * The icon and glossary chips point at pages that only exist in English (the glossary page is
 * English in every locale, icon pages carry English H1s), so on non-EN pages they read as
 * untranslated copy (review round 3). They stay EN-only until those pages are localised; the
 * bag guides are localised in all six locales and render everywhere.
 *
 * Heading outline: the page h1 is the title above the results; this block is one h2 with
 * h3 sections (intro sub-headings, FAQ, guides) and h4 leaves (questions, card titles).
 * Review round 2 asked for the block to read as supporting content, quiet like the top of
 * the footer: every heading in it renders at h4 size (18/28) whatever its outline level,
 * questions at 16px bold, prose capped at a readable width and the vertical rhythm halved.
 * Same text, same headings, same schema; only the styling is quieter.
 */
export const PlpSeoBlock = ({
  heading,
  subheading,
  description,
  pagePath,
  generatedIntro,
}: PlpSeoBlockProps) => {
  const { t } = useTranslation();
  const locale = useContentLocale();

  const { introMarkdown, questions, guideIndex } =
    splitListingDescription(description);

  /*
   * Chips are not repeated when the prose already links the same page. Guides are exempt: a
   * guide cited inline in a sentence and shown as a card are different surfaces, and on
   * /bags five of the six curated guides are cited in the copy, which left a single card.
   */
  const proseMarkdown = [
    introMarkdown,
    ...questions.map(question => question.answerMarkdown),
  ].join("\n");
  const editorial = getPlpEditorialLinks(locale, pagePath ?? undefined) ?? [];
  const guides = editorial.filter(isGuide);
  const chips = showsEditorialChips(locale)
    ? filterLinksAgainstDescription(
        editorial.filter(link => !isGuide(link)),
        proseMarkdown
      )
    : [];
  const shownGuideHrefs = new Set(guides.map(guide => guide.href));
  const moreGuides = guideIndex.filter(
    guide => !shownGuideHrefs.has(guide.href)
  );

  // WHO-5764: brand pages link the price and authenticity guides on the brand's icon pages.
  const iconGuides = getIconGuideLinks(locale, pagePath);

  const fallbackIntro = introMarkdown ? null : generatedIntro;
  const hasCopy = Boolean(
    heading || subheading || introMarkdown || fallbackIntro
  );
  const hasGuides = guides.length > 0 || moreGuides.length > 0;

  if (
    !hasCopy &&
    questions.length === 0 &&
    !hasGuides &&
    chips.length === 0 &&
    iconGuides.length === 0
  ) {
    return null;
  }

  return (
    <section
      className={PLP_SEO_SECTION_CLASS}
      aria-labelledby={heading ? PLP_SEO_HEADING_ID : undefined}
    >
      {hasCopy && (
        <div className={`flex flex-col gap-2 ${PLP_SEO_TEXT_WIDTH_CLASS}`}>
          {heading && (
            <Heading
              as="h2"
              size={PLP_SEO_SECTION_HEADING_SIZE}
              id={PLP_SEO_HEADING_ID}
            >
              {heading}
            </Heading>
          )}
          {subheading && (
            <p className="font-body text-base leading-6 text-grey-800">
              {subheading}
            </p>
          )}
          {introMarkdown && <CollapsibleIntro markdown={introMarkdown} />}
          {fallbackIntro && (
            <p className="font-body text-base leading-6 text-grey-800">
              {fallbackIntro}
            </p>
          )}
        </div>
      )}

      <FaqAccordion questions={questions} />

      {hasGuides && (
        <div className="flex flex-col gap-3">
          <Heading
            as="h3"
            size={PLP_SEO_SECTION_HEADING_SIZE}
            id={GUIDES_HEADING_ID}
          >
            {t(
              PLP_SEO_KEYS.guidesHeading,
              plpSeoFallback.guidesHeading(locale)
            )}
          </Heading>
          <GuideCards guides={guides} more={moreGuides} />
        </div>
      )}

      {chips.length > 0 && (
        <LinkChips
          heading={
            hasGuides
              ? undefined
              : t(
                  PLP_SEO_KEYS.guidesHeading,
                  plpSeoFallback.guidesHeading(locale)
                )
          }
          headingAs="h3"
          links={chips}
        />
      )}

      <LinkChips
        heading={t(
          PLP_SEO_KEYS.iconGuidesHeading,
          plpSeoFallback.iconGuidesHeading(locale)
        )}
        headingAs="h3"
        links={iconGuides}
      />
    </section>
  );
};
